import { useState, useEffect } from 'react';
import { Send } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogBody,
  DialogFooter,
  DialogClose,
  Button,
  Input,
  Label,
  Spinner,
  Switch,
  Checkbox,
  SegmentedControl,
} from '@vnetwork/ui/primitives';
import { Alert } from '@vnetwork/ui/feedback';
import { Field } from '@vnetwork/ui/forms';
import {
  useSendReconciliationEmail,
  useSendStatus,
  useEmailPreview,
  type BatchStatus,
  type SendEmailScope,
} from '@/api/reconciliation';
import { NumericInput } from '@/components/numeric-input';

/* ── Props ───────────────────────────────────────────────────────────────── */

export interface SendEmailDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** cohort_key of the cycle, e.g. "2026-07-23_2026-08-22". */
  cohortKey: string;
  /** Human-readable period label shown in the dialog title. */
  periodLabel: string;
  /**
   * When set, the dialog opens pre-scoped to this single invoice_id.
   * The scope selector is hidden and scope is forced to 'invoice_ids'.
   */
  singleInvoiceId?: number;
}

/* ── Scope options ───────────────────────────────────────────────────────── */

const SCOPE_ITEMS: { value: SendEmailScope; label: string }[] = [
  { value: 'all', label: 'All' },
  { value: 'pending', label: 'Pending only' },
  { value: 'invoice_ids', label: 'Single invoice' },
];

/* ── Component ───────────────────────────────────────────────────────────── */

/**
 * Send-email dialog for a reconciliation cycle.
 *
 * Scope selector: 'all' | 'pending' | 'invoice_ids'.
 * When scope='invoice_ids', an invoice_id input is shown and email preview is
 * enabled (useEmailPreview). Preview fetches lazily on demand.
 *
 * Safety gates:
 *   - Live sends (test_recipient=="") require confirm checkbox.
 *   - 409 batch-already-active → inline error.
 *   - Polling: after successful submit we poll send-status every 3 s.
 */
export function ReconciliationSendEmailDialog({
  open,
  onOpenChange,
  cohortKey,
  periodLabel,
  singleInvoiceId,
}: SendEmailDialogProps) {
  /* ── Form state ─────────────────────────────────────────────────────────── */
  const isSingleMode = singleInvoiceId !== undefined;

  // When opened for a single invoice, lock scope to invoice_ids
  const [scope, setScope] = useState<SendEmailScope>(
    isSingleMode ? 'invoice_ids' : 'all',
  );
  // invoice_id for invoice_ids scope — pre-filled when in single mode
  const [invoiceIdInput, setInvoiceIdInput] = useState(
    isSingleMode ? String(singleInvoiceId) : '',
  );
  const [showPreview, setShowPreview] = useState(false);

  const [testRecipient, setTestRecipient] = useState('');
  const [scheduleAt, setScheduleAt] = useState('');
  const [forceResend, setForceResend] = useState(false);
  const [confirmed, setConfirmed] = useState(false);

  /* ── Mutation + polling state ───────────────────────────────────────────── */
  const [batchId, setBatchId] = useState('');
  const [submitError, setSubmitError] = useState<string | null>(null);

  const mutation = useSendReconciliationEmail(cohortKey);

  /* Email preview — enabled only when scope=invoice_ids and invoiceId is valid */
  const parsedInvoiceId = parseInt(invoiceIdInput, 10);
  const previewInvoiceId =
    scope === 'invoice_ids' && !Number.isNaN(parsedInvoiceId) && parsedInvoiceId > 0
      ? parsedInvoiceId
      : 0;

  const emailPreview = useEmailPreview(cohortKey, previewInvoiceId, {
    enabled: showPreview && previewInvoiceId > 0,
  });

  /* Poll once we have a batchId — every 3 s, stop when status is terminal. */
  const isTest = testRecipient.trim().length > 0;
  const pollEnabled = Boolean(batchId) && mutation.isSuccess;

  const statusQ = useSendStatus(cohortKey, batchId, {
    enabled: pollEnabled,
    refetchInterval: pollEnabled ? 3_000 : undefined,
  });

  const batchStatus: BatchStatus | undefined = statusQ.data;
  const isDone =
    batchStatus?.status === 'done' ||
    batchStatus?.status === 'completed' ||
    batchStatus?.status === 'failed';

  // isDone is read for future reference (polling stops naturally via enabled flag)
  void isDone;

  /* Reset state when dialog closes. */
  useEffect(() => {
    if (!open) {
      setScope(isSingleMode ? 'invoice_ids' : 'all');
      setInvoiceIdInput(isSingleMode && singleInvoiceId ? String(singleInvoiceId) : '');
      setShowPreview(false);
      setTestRecipient('');
      setScheduleAt('');
      setForceResend(false);
      setConfirmed(false);
      setBatchId('');
      setSubmitError(null);
      mutation.reset();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  /* ── Submit ─────────────────────────────────────────────────────────────── */

  const handleSubmit = async () => {
    setSubmitError(null);

    const trimmedTest = testRecipient.trim();

    const invoiceIds: number[] =
      scope === 'invoice_ids' && previewInvoiceId > 0 ? [previewInvoiceId] : [];

    try {
      const result = await mutation.mutateAsync({
        scope,
        invoice_ids: scope === 'invoice_ids' ? invoiceIds : undefined,
        test_recipient: trimmedTest || null,
        schedule_at: scheduleAt || null,
        force_resend: forceResend,
        confirm: trimmedTest ? undefined : true,
      });

      const id = result?.batch_id ?? '';
      if (id) setBatchId(id);
    } catch (err: unknown) {
      const axiosError = err as {
        response?: { status?: number; data?: { message?: string; error?: string } };
        message?: string;
      };
      const status = axiosError?.response?.status;
      const msg =
        axiosError?.response?.data?.message ||
        axiosError?.response?.data?.error ||
        axiosError?.message ||
        'An unexpected error occurred.';

      if (status === 409) {
        setSubmitError(
          'A send batch is already in progress. Please wait for it to complete before sending again.',
        );
      } else if (status === 400) {
        setSubmitError(`Send failed: ${msg}`);
      } else {
        setSubmitError(msg);
      }
    }
  };

  /* ── Computed ────────────────────────────────────────────────────────────── */

  const canSubmit =
    isTest ||
    (confirmed &&
      (scope !== 'invoice_ids' || previewInvoiceId > 0));
  const isSubmitting = mutation.isPending;
  const hasResult = mutation.isSuccess;

  const scopeLabel =
    scope === 'all'
      ? 'all customers'
      : scope === 'pending'
        ? 'pending-only customers'
        : previewInvoiceId > 0
          ? `invoice #${previewInvoiceId}`
          : '(no invoice selected)';

  /* ── Render ─────────────────────────────────────────────────────────────── */

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent size="md">
        <DialogHeader>
          <DialogTitle>
            Send reconciliation email — {periodLabel}
          </DialogTitle>
        </DialogHeader>

        <DialogBody>
          <div style={{ display: 'grid', gap: 'var(--vn-space-4)' }}>
            {/* Scope selector (hidden in single-invoice mode) */}
            {!isSingleMode && (
              <Field label="Send to">
                {() => (
                  <SegmentedControl
                    value={scope}
                    onValueChange={(v) => {
                      if (!isSubmitting && !hasResult) {
                        setScope(v as SendEmailScope);
                        setShowPreview(false);
                      }
                    }}
                    items={SCOPE_ITEMS}
                  />
                )}
              </Field>
            )}

            {/* Invoice ID input when scope = invoice_ids */}
            {scope === 'invoice_ids' && !isSingleMode && (
              <Field
                label="Invoice ID"
                description="Send email for this specific invoice only."
              >
                {(p) => (
                  <NumericInput thousandSeparator={false} precision={0}
                    {...p}
                    value={invoiceIdInput}
                    onChange={(e) => {
                      setInvoiceIdInput(e.target.value);
                      setShowPreview(false);
                    }}
                    placeholder="e.g. 122111"
                    disabled={isSubmitting || hasResult}
                  />
                )}
              </Field>
            )}

            {/* Email preview — only for invoice_ids scope */}
            {scope === 'invoice_ids' && previewInvoiceId > 0 && !hasResult && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--vn-space-2)' }}>
                {!showPreview ? (
                  <Button
                    variant="secondary"
                    size="sm"
                    disabled={isSubmitting}
                    onClick={() => {
                      setShowPreview(true);
                      if (!emailPreview.isFetched) {
                        emailPreview.refetch();
                      }
                    }}
                  >
                    Preview email
                  </Button>
                ) : (
                  <div>
                    <div
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 'var(--vn-space-2)',
                        marginBottom: 'var(--vn-space-2)',
                      }}
                    >
                      <span
                        style={{
                          fontSize: 'var(--vn-font-size-sm)',
                          color: 'var(--vn-fg-muted)',
                        }}
                      >
                        Email preview
                        {emailPreview.data?.recipients?.length
                          ? ` · To: ${emailPreview.data.recipients.join(', ')}`
                          : ''}
                      </span>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setShowPreview(false)}
                      >
                        Hide
                      </Button>
                    </div>
                    {emailPreview.isLoading ? (
                      <div
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 'var(--vn-space-2)',
                          color: 'var(--vn-fg-muted)',
                          fontSize: 'var(--vn-font-size-sm)',
                        }}
                      >
                        <Spinner size="sm" />
                        Loading preview…
                      </div>
                    ) : emailPreview.isError ? (
                      <Alert tone="danger" title="Preview failed">
                        Unable to load email preview. You can still send the email.
                      </Alert>
                    ) : emailPreview.data ? (
                      <iframe
                        srcDoc={emailPreview.data.html}
                        style={{
                          width: '100%',
                          height: 360,
                          border: '1px solid var(--vn-border-subtle)',
                          borderRadius: 'var(--vn-radius-md)',
                          background: 'var(--vn-bg-surface)',
                        }}
                        title="Email preview"
                        sandbox="allow-same-origin"
                      />
                    ) : null}
                  </div>
                )}
              </div>
            )}

            {/* Test recipient */}
            <Field
              label={
                <>
                  Test email address{' '}
                  <span style={{ color: 'var(--vn-fg-muted)', fontWeight: 'var(--vn-font-weight-regular)' }}>
                    (leave blank to send to {scopeLabel})
                  </span>
                </>
              }
              description={isTest ? 'Test mode: email will only be sent to the address above.' : undefined}
            >
              {(p) => (
                <Input
                  {...p}
                  type="email"
                  value={testRecipient}
                  onChange={(e) => setTestRecipient(e.target.value)}
                  placeholder="test@example.com"
                  disabled={isSubmitting || hasResult}
                />
              )}
            </Field>

            {/* Schedule at */}
            <Field
              label={
                <>
                  Schedule send{' '}
                  <span style={{ color: 'var(--vn-fg-muted)', fontWeight: 'var(--vn-font-weight-regular)' }}>
                    (optional — sends immediately if left blank)
                  </span>
                </>
              }
            >
              {(p) => (
                <Input
                  {...p}
                  type="datetime-local"
                  value={scheduleAt}
                  onChange={(e) => setScheduleAt(e.target.value)}
                  disabled={isSubmitting || hasResult}
                />
              )}
            </Field>

            {/* Force resend */}
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 'var(--vn-space-2)',
                justifyContent: 'space-between',
              }}
            >
              <div>
                <Label htmlFor="force-resend" style={{ cursor: 'pointer' }}>
                  Resend even if already sent
                </Label>
                <p style={{ fontSize: 'var(--vn-font-size-xs)', color: 'var(--vn-fg-muted)', margin: 0 }}>
                  Bypasses the &ldquo;already sent&rdquo; check for this cycle.
                </p>
              </div>
              <Switch
                id="force-resend"
                checked={forceResend}
                onCheckedChange={setForceResend}
                disabled={isSubmitting || hasResult}
              />
            </div>

            {/* Live-send confirm gate (only shown when not in test mode) */}
            {!isTest && (
              <Alert tone="warning">
                <label
                  style={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: 'var(--vn-space-2)',
                    cursor: 'pointer',
                  }}
                >
                  <Checkbox
                    checked={confirmed}
                    onCheckedChange={(v) => setConfirmed(v === true)}
                    disabled={isSubmitting || hasResult}
                    style={{ marginTop: 'var(--vn-space-1)', flexShrink: 0 }}
                  />
                  <span style={{ fontSize: 'var(--vn-font-size-sm)', color: 'var(--vn-fg-default)', lineHeight: 1.5 }}>
                    I confirm I want to send reconciliation emails to{' '}
                    <strong>{scopeLabel}</strong> in this cycle.
                    This action cannot be undone.
                  </span>
                </label>
              </Alert>
            )}

            {/* Submit error */}
            {submitError && (
              <Alert tone="danger" title="Send failed">
                {submitError}
              </Alert>
            )}

            {/* Success / polling result */}
            {hasResult && !submitError && (
              <Alert tone="success" title="Send command accepted">
                {batchId ? (
                  <>
                    Batch ID: <code style={{ fontFamily: 'monospace' }}>{batchId}</code>
                    {batchStatus ? (
                      <div style={{ marginTop: 'var(--vn-space-1)' }}>
                        Status: <strong>{batchStatus.status}</strong>
                        {typeof batchStatus.sent === 'number' && (
                          <> · Sent: {batchStatus.sent}/{batchStatus.total}</>
                        )}
                        {typeof batchStatus.failed === 'number' && batchStatus.failed > 0 && (
                          <> · Failed: {batchStatus.failed}</>
                        )}
                      </div>
                    ) : (
                      <div style={{ marginTop: 'var(--vn-space-1)', color: 'var(--vn-fg-muted)' }}>
                        Fetching status…
                      </div>
                    )}
                  </>
                ) : (
                  'Email has been scheduled for delivery.'
                )}
              </Alert>
            )}
          </div>
        </DialogBody>

        <DialogFooter>
          <DialogClose asChild>
            <Button variant="secondary" disabled={isSubmitting}>
              {hasResult ? 'Close' : 'Cancel'}
            </Button>
          </DialogClose>
          {!hasResult && (
            <Button
              onClick={handleSubmit}
              disabled={!canSubmit || isSubmitting}
              startIcon={<Send size={14} />}
            >
              {isSubmitting
                ? 'Sending…'
                : isTest
                  ? 'Send test'
                  : 'Send reconciliation email'}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
